import Dexie, { Table } from 'dexie';

// IndexedDB schema for encrypted local-first budget storage
export interface DocChunk {
  id?: number; // Auto-increment
  ledgerId: string;
  seq: number; // Sequence number for ordering chunks
  bytes: Uint8Array; // Encrypted chunk data
}

export interface BlobEntry {
  hash: string; // Primary key - hash of the original data
  bytes: Uint8Array; // Encrypted blob data (receipts, attachments, etc)
}

export interface Snapshot {
  id?: number; // Auto-increment
  ledgerId: string;
  ts: number; // Timestamp
  bytes: Uint8Array; // Encrypted snapshot data
}

export interface LedgerMetadata {
  id: string; // Ledger ID (primary key)
  salt: Uint8Array; // Salt for key derivation
  encDEK: Uint8Array; // Encrypted data encryption key
  householdName: string; // Unencrypted household name for display
  createdAt: number; // Creation timestamp
  lastAccessed: number; // Last access timestamp
  version: number; // Schema version
}

export class BudgetDatabase extends Dexie {
  docChunks!: Table<DocChunk>;
  blobs!: Table<BlobEntry>;
  snapshots!: Table<Snapshot>;
  metadata!: Table<LedgerMetadata>;

  constructor() {
    super('budget-ledger');
    
    this.version(1).stores({
      docChunks: '++id, ledgerId, seq, bytes',
      blobs: 'hash, bytes',
      snapshots: '++id, ledgerId, ts, bytes',
      metadata: 'id, salt, encDEK, householdName, createdAt, lastAccessed, version'
    });
  }

  // Get all chunks for a ledger, ordered by sequence
  async getDocumentChunks(ledgerId: string): Promise<DocChunk[]> {
    return await this.docChunks
      .where('ledgerId')
      .equals(ledgerId)
      .sortBy('seq');
  }

  // Store document chunks (replace all for atomic update)
  async storeDocumentChunks(ledgerId: string, chunks: Uint8Array[]): Promise<void> {
    await this.transaction('rw', this.docChunks, async () => {
      // Delete old chunks
      await this.docChunks.where('ledgerId').equals(ledgerId).delete();
      
      // Insert new chunks
      const docChunks: DocChunk[] = chunks.map((bytes, index) => ({
        ledgerId,
        seq: index,
        bytes
      }));
      
      await this.docChunks.bulkAdd(docChunks);
    });
  }

  // Store a blob with deduplication
  async storeBlob(hash: string, bytes: Uint8Array): Promise<void> {
    // Only store if not already exists
    const existing = await this.blobs.get(hash);
    if (!existing) {
      await this.blobs.put({ hash, bytes });
    }
  }

  // Get a blob by hash
  async getBlob(hash: string): Promise<Uint8Array | null> {
    const entry = await this.blobs.get(hash);
    return entry ? entry.bytes : null;
  }

  // Store a snapshot
  async storeSnapshot(ledgerId: string, bytes: Uint8Array): Promise<void> {
    await this.snapshots.add({
      ledgerId,
      ts: Date.now(),
      bytes
    });
    
    // Keep only the last 10 snapshots per ledger
    const allSnapshots = await this.snapshots
      .where('ledgerId')
      .equals(ledgerId)
      .sortBy('ts');
      
    if (allSnapshots.length > 10) {
      const toDelete = allSnapshots.slice(0, allSnapshots.length - 10);
      const idsToDelete = toDelete.map(s => s.id!);
      await this.snapshots.bulkDelete(idsToDelete);
    }
  }

  // Get the latest snapshot
  async getLatestSnapshot(ledgerId: string): Promise<Uint8Array | null> {
    const snapshots = await this.snapshots
      .where('ledgerId')
      .equals(ledgerId)
      .reverse()
      .sortBy('ts');
    
    const snapshot = snapshots.length > 0 ? snapshots[0] : null;
      
    return snapshot ? snapshot.bytes : null;
  }

  // Store ledger metadata
  async storeLedgerMetadata(metadata: LedgerMetadata): Promise<void> {
    await this.metadata.put(metadata);
  }

  // Get ledger metadata
  async getLedgerMetadata(ledgerId: string): Promise<LedgerMetadata | null> {
    const metadata = await this.metadata.get(ledgerId);
    if (metadata) {
      // Update last accessed time
      metadata.lastAccessed = Date.now();
      await this.metadata.put(metadata);
    }
    return metadata || null;
  }

  // List all ledgers (for household selection)
  async listLedgers(): Promise<Pick<LedgerMetadata, 'id' | 'householdName' | 'createdAt' | 'lastAccessed'>[]> {
    const items = await this.metadata
      .reverse()
      .sortBy('lastAccessed');
      
    return items.map(item => ({
        id: item.id,
        householdName: item.householdName,
        createdAt: item.createdAt,
        lastAccessed: item.lastAccessed
      }));
  }

  // Delete a ledger completely
  async deleteLedger(ledgerId: string): Promise<void> {
    await this.transaction('rw', [this.docChunks, this.snapshots, this.metadata], async () => {
      await this.docChunks.where('ledgerId').equals(ledgerId).delete();
      await this.snapshots.where('ledgerId').equals(ledgerId).delete();
      await this.metadata.delete(ledgerId);
    });
  }

  // Clean up old data (run periodically)
  async cleanup(): Promise<void> {
    const cutoff = Date.now() - (90 * 24 * 60 * 60 * 1000); // 90 days ago
    
    // Delete old snapshots
    await this.snapshots.where('ts').below(cutoff).delete();
    
    // Note: We don't auto-delete old ledgers, user must explicitly delete them
  }
}

// Singleton instance
let database: BudgetDatabase | null = null;

export const getDatabase = (): BudgetDatabase => {
  if (!database) {
    database = new BudgetDatabase();
  }
  return database;
};